// Un producto de la tanda, como una tarjeta 9:16 más ancha que la del Catálogo (un video se
// lee de más lejos), con la voz en un contenedor blanco PEGADO AL PIE DE LA TARJETA (adentro,
// sobre el contenido) y DEBAJO el nombre con la miniatura del producto
// (ADR-2026-10-30-videos-cortos-con-el-agente). La misma anatomía que `TarjetaDeToma`:
//  • sin guion: la claqueta y "Sin guion" (o "Escribiendo…" mientras el escritor trabaja);
//  • con guion: dos contenedores blancos que se reparten el alto, "Escenas" (la idea y las
//    tomas en español) y "Guion" (lo que dice la voz), cada uno recortado con "…" en la última
//    línea que entra. El prompt en inglés no va en la tarjeta: está en el panel. Que el guion
//    esté fijado (editado a mano o por el agente) no se muestra en la tarjeta: la persona no
//    distingue "editado" de "escrito", y el efecto (el escritor no lo pisa) se cuenta en el panel;
//  • en cola o creándose: sólo el recuadro de carga de la app, sin la voz ni la etapa encima
//    (la etapa se lee en el panel);
//  • listo: el video con su póster y controles, y descargar;
//  • falló o cancelado: el aviso con el motivo.
// Tocar la tarjeta abre el panel; tocar la pastilla de voz abre el selector para ESE producto.
import { AlertCircle, ChevronRight, Clapperboard, Download, Loader2, Maximize2, Mic, Pencil, Play, Quote, RefreshCw, VolumeX, X } from 'lucide-react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefCallback } from 'react';
import CachedImg from '../../../../components/CachedImg';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import { RecuadroGenerando } from '../../components/PanelTandaCreaciones';
import { PREFIJO_REESCRITURA_FALLIDA, type ProductoDeLaTanda } from './plan';
import type { VideoFirmado } from './useVideosFirmados';

const ESTADO_TEXTO: Record<ProductoDeLaTanda['estado'], string> = {
  'sin-guion': 'Sin guion',
  escrito: 'Guion listo',
  en_cola: 'En la fila',
  generando: 'Creando…',
  listo: 'Listo',
  fallo: 'No salió',
  cancelado: 'Cancelado',
};

/** Lo que tarda un video, para la barra del recuadro de carga (~5 min). */
const DURACION_ESTIMADA_MS = 5 * 60_000;
export const ASPECTO_VIDEO = '9 / 16';
/** El ancho de cada tarjeta: en el teléfono dos por fila; en desktop 240 px (la del Catálogo mide 168). */
export const ANCHO_TARJETA_VIDEO = 'w-full sm:w-[240px]';
/**
 * El aire al pie del contenido, para el contenedor de la voz que va pegado abajo adentro de la
 * tarjeta: 8 px de margen + 48 de la voz + 8 de separación, la misma que entre los contenedores
 * de la hoja y que el margen de la tarjeta.
 */
const ESPACIO_PARA_LA_VOZ = 'pb-[64px]';
const ETIQUETA_DE_LA_HOJA = 'inline-flex w-fit items-center gap-1 rounded-md bg-[rgb(var(--brand-primary-rgb)_/_0.095)] py-0.5 px-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-brand-primary';
/**
 * Los dos contenedores de la hoja (escenas y guion): blancos sobre el fondo de la tarjeta, como
 * el de la voz. Crecen para llenar el alto (lo que sobra de la última línea entera se reparte),
 * así el de abajo termina siempre a la misma distancia de la voz.
 */
const CONTENEDOR_DE_LA_HOJA = 'flex shrink-0 grow flex-col gap-1.5 rounded-md bg-white p-2.5 ring-1 ring-black/[0.06]';
/** El padding de abajo de un contenedor (`p-2.5`). */
const PADDING_DEL_CONTENEDOR = 10;
const TEXTO_DE_LA_HOJA = 'text-[12px] leading-[1.45] text-[#3C3C43]/75';
/** El alto de una línea de texto de la hoja (12 px × 1.45). */
const ALTO_DE_LINEA = 12 * 1.45;
/** El espacio entre los dos contenedores (`gap-2`). */
const ESPACIO_ENTRE_CONTENEDORES = 8;
/** Recorta el texto con "…" en la línea `lineas`. */
const recorte = (lineas: number): CSSProperties => ({ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lineas, overflow: 'hidden' });

/**
 * Cuántas líneas muestra cada parte de la hoja: el alto se reparte a medias entre las escenas y
 * el guion, y si una entra entera en menos de su mitad, la otra se lleva lo que sobra. Cada una
 * se recorta con "…" en su última línea. Se mide al cambiar el tamaño del área (el ancho de la
 * tarjeta) o el texto. El largo entero sale de `scrollHeight`, que en un texto con line-clamp
 * cuenta también lo recortado. El área es un ref de callback porque la hoja aparece recién
 * cuando el escritor termina: el observer se engancha cuando el nodo se monta.
 */
const useRepartoDeLaHoja = (claveDelTexto: string) => {
  const refDeEscenas = useRef<HTMLSpanElement | null>(null);
  const refDelGuion = useRef<HTMLSpanElement | null>(null);
  const area = useRef<HTMLSpanElement | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const [lineas, setLineas] = useState<[number, number]>([6, 4]);
  const medir = useCallback(() => {
    const a = area.current;
    const e = refDeEscenas.current;
    if (!a || !e) return;
    const g = refDelGuion.current;
    // Lo que ocupa cada contenedor además de su texto: el padding y la etiqueta. Se mide desde
    // arriba del contenedor hasta el texto (el contenedor crece, su alto no sirve).
    const marco = (t: HTMLElement) => t.getBoundingClientRect().top - (t.parentElement?.getBoundingClientRect().top ?? 0) + PADDING_DEL_CONTENEDOR;
    const libre = a.clientHeight - marco(e) - (g ? marco(g) + ESPACIO_ENTRE_CONTENEDORES : 0);
    const total = Math.max(g ? 2 : 1, Math.floor(libre / ALTO_DE_LINEA));
    const enteras = (t: HTMLElement) => Math.max(1, Math.round(t.scrollHeight / ALTO_DE_LINEA));
    let nuevas: [number, number];
    if (!g) nuevas = [total, 0];
    else {
      const deEscenas = Math.max(1, Math.min(enteras(e), Math.max(Math.floor(total / 2), total - enteras(g))));
      nuevas = [deEscenas, Math.max(1, Math.min(enteras(g), total - deEscenas))];
    }
    setLineas((prev) => (prev[0] === nuevas[0] && prev[1] === nuevas[1] ? prev : nuevas));
  }, []);
  const refDelArea = useCallback<RefCallback<HTMLSpanElement>>(
    (el) => {
      observer.current?.disconnect();
      observer.current = null;
      area.current = el;
      if (!el || typeof ResizeObserver === 'undefined') return;
      medir();
      observer.current = new ResizeObserver(medir);
      observer.current.observe(el);
    },
    [medir],
  );
  useLayoutEffect(medir, [medir, claveDelTexto]);
  // Red de seguridad: si con esas líneas el área igual desborda (el alto de las etiquetas cambia cuando carga la
  // fuente, o un redondeo), se saca una línea de la parte más larga hasta que entre. Sin esto el recuadro de abajo
  // quedaba cortado, sin su margen.
  useLayoutEffect(() => {
    const a = area.current;
    if (!a || a.scrollHeight - a.clientHeight <= 1) return;
    setLineas(([e, g]) => (g > 1 && g >= e ? [e, g - 1] : e > 1 ? [e - 1, g] : [e, g]));
  }, [lineas]);
  // La fuente de la marca llega después del primer dibujo: se vuelve a medir con la de verdad.
  useEffect(() => {
    let vivo = true;
    void document.fonts?.ready.then(() => vivo && medir());
    return () => {
      vivo = false;
    };
  }, [medir]);
  return { refDelArea, refDeEscenas, refDelGuion, lineas };
};

/**
 * La etiqueta roja de una tarjeta con algo que no anduvo: el video no salió, se canceló, no entró
 * en la tanda ("No se creó") o el escritor no pudo reescribir el guion (quedó el que había).
 */
const textoDelAviso = (estado: ProductoDeLaTanda['estado'], motivo?: string): string =>
  estado === 'cancelado' ? 'Cancelado' : estado === 'fallo' ? 'No salió' : motivo?.startsWith(PREFIJO_REESCRITURA_FALLIDA) ? 'No se pudo reescribir' : 'No se creó';

interface Etiqueta {
  icono: ReactNode;
  texto: string;
  quitable?: boolean;
  alerta?: boolean;
}

export const TarjetaDeVideo: React.FC<{
  producto: ProductoDeLaTanda;
  /** Cómo se lee la voz efectiva ("Malena", "Automática", "Sin voz"). */
  nombreDeVoz: string;
  video?: VideoFirmado | null;
  activa: boolean;
  escribiendo?: boolean;
  onAbrir: () => void;
  onElegirVoz: () => void;
  onQuitarRehacer?: () => void;
  onDescargar?: () => void;
  descargando?: boolean;
  /** Abre el video en el visor ampliado de la app (el de las creaciones). */
  onExpandir?: () => void;
}> = ({ producto, nombreDeVoz, video, activa, escribiendo = false, onAbrir, onElegirVoz, onQuitarRehacer, onDescargar, descargando = false, onExpandir }) => {
  const { estado, guion } = producto;
  const titulo = `${producto.nombre}: ${ESTADO_TEXTO[estado]}${producto.fijado ? ' (guion editado)' : ''}${producto.motivo ? ` — ${producto.motivo}` : ''}`;
  const escribiendoEste = escribiendo && !producto.fijado && !guion && estado !== 'fallo';
  const anillo = activa ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06]';
  const [desde] = useState(() => producto.lanzadoEn ?? Date.now());
  const enVuelo = estado === 'en_cola' || estado === 'generando';
  const sinVoz = nombreDeVoz === 'Sin voz';
  const esTelefono = useDebajoDeMd();
  const locucion = guion?.locucion?.texto?.trim() ?? '';
  const { refDelArea, refDeEscenas, refDelGuion, lineas } = useRepartoDeLaHoja(guion ? [guion.idea, ...guion.escenas, locucion].join('\n') : '');

  // Como en `TarjetaDeToma`: la etiqueta de arriba va sólo sobre una tarjeta CON contenido (el
  // guion o el video). Sin guion, el cuerpo ya dice el estado y la etiqueta lo repetiría.
  const etiqueta: Etiqueta | null = enVuelo || (!guion && estado !== 'listo')
    ? null
    : producto.rehacer
      ? { icono: <RefreshCw className="h-3 w-3" strokeWidth={2.25} />, texto: producto.soloVoz ? 'Voz por rehacer' : 'Por rehacer', quitable: true }
      : estado === 'fallo' || estado === 'cancelado' || (producto.motivo && guion)
          ? { icono: <AlertCircle className="h-3 w-3" strokeWidth={2.25} />, texto: textoDelAviso(estado, producto.motivo), alerta: true }
          : null;

  return (
    <figure className={`animate-fade-in group flex shrink-0 flex-col gap-2.5 ${ANCHO_TARJETA_VIDEO}`}>
      <div className={`relative w-full overflow-hidden rounded-xl bg-[#F5F6FA] transition-[box-shadow,transform] hover:-translate-y-px hover:shadow-md ${anillo}`} style={{ aspectRatio: ASPECTO_VIDEO }}>
        {enVuelo ? (
          <button type="button" onClick={onAbrir} title={titulo} aria-label={titulo} className="relative block h-full w-full text-left">
            <RecuadroGenerando ratio={9 / 16} creadaEn={producto.lanzadoEn ?? desde} esVideo duracionMs={DURACION_ESTIMADA_MS} />
          </button>
        ) : estado === 'listo' && video && esTelefono && onExpandir ? (
          // En el teléfono, como en Creaciones: el póster con un play, y tocarlo abre el visor de la app, donde se
          // reproduce. Sin reproductor ni botones chicos en la tarjeta (ADR-2026-10-31-videos-cortos-en-el-telefono-se-ve-en-grande).
          <button type="button" onClick={onExpandir} aria-label={`Ver el video de ${producto.nombre}`} className="relative block h-full w-full">
            {video.poster ? (
              <CachedImg src={video.poster} alt="" className="h-full w-full bg-neutral-100 object-cover" />
            ) : (
              <video src={video.url} muted playsInline preload="metadata" className="pointer-events-none h-full w-full object-cover" />
            )}
            <span className="absolute inset-0 grid place-items-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-black/40 text-white backdrop-blur-sm">
                <Play className="ml-0.5 h-6 w-6 fill-current" strokeWidth={0} aria-hidden="true" />
              </span>
            </span>
          </button>
        ) : estado === 'listo' && video ? (
          // Sin el menú de tres puntos ni la pantalla completa del navegador: ampliar abre el visor de la app (el botón
          // de arriba). Sin descarga, velocidad, imagen en imagen ni transmitir, Chrome no dibuja el menú.
          <video
            src={video.url}
            poster={video.poster ?? undefined}
            controls
            controlsList="nodownload nofullscreen noplaybackrate noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            playsInline
            preload="metadata"
            className="sin-pantalla-completa h-full w-full object-cover"
          />
        ) : estado === 'listo' ? (
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-brand-primary" aria-hidden="true" />
          </div>
        ) : guion ? (
          // El guion escrito, sin video todavía: una hoja con dos contenedores que se reparten el
          // alto, no un hueco de video.
          <button type="button" onClick={onAbrir} title={titulo} aria-label={`Ver y editar el guion de ${producto.nombre}`} className={`flex h-full w-full flex-col p-2 text-left ${etiqueta ? 'pt-9' : ''} ${ESPACIO_PARA_LA_VOZ}`}>
            <span ref={refDelArea} className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              <span className={CONTENEDOR_DE_LA_HOJA}>
                <span className={ETIQUETA_DE_LA_HOJA}>
                  <Clapperboard className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
                  Escenas
                </span>
                <span ref={refDeEscenas} className={TEXTO_DE_LA_HOJA} style={recorte(lineas[0])}>
                  {guion.idea && (
                    <>
                      <span className="text-brand-black">{guion.idea}</span>
                      {guion.escenas.length > 0 && <br />}
                    </>
                  )}
                  {guion.escenas.map((e, i) => (
                    <Fragment key={i}>
                      {i > 0 && <br />}
                      {i + 1}. {e}
                    </Fragment>
                  ))}
                </span>
              </span>
              {locucion && (
                <span className={CONTENEDOR_DE_LA_HOJA}>
                  <span className={ETIQUETA_DE_LA_HOJA}>
                    <Quote className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
                    Guion
                  </span>
                  <span ref={refDelGuion} className={`${TEXTO_DE_LA_HOJA} italic`} style={recorte(lineas[1])}>
                    “{locucion}”
                  </span>
                </span>
              )}
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onAbrir}
            title={titulo}
            aria-label={titulo}
            className={`flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center ${ESPACIO_PARA_LA_VOZ} ${estado === 'fallo' ? 'bg-[#FFF5F4]' : 'bg-[#F5F6FA]'}`}
          >
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-[rgb(var(--brand-black-rgb)_/_0.65)] shadow-sm">
              <Clapperboard className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
            </span>
            <span className={`inline-flex items-center gap-1 text-[12px] font-semibold ${estado === 'fallo' ? 'text-[#B42318]' : 'text-[#3C3C43]/60'}`}>
              {escribiendoEste ? <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-primary" strokeWidth={2.25} aria-hidden="true" /> : estado === 'fallo' ? <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" /> : null}
              {escribiendoEste ? 'Escribiendo…' : ESTADO_TEXTO[estado]}
            </span>
          </button>
        )}
        {etiqueta && (
          <span
            title={producto.motivo ?? etiqueta.texto}
            className={`absolute left-1.5 top-1.5 flex max-w-[calc(100%-2.75rem)] items-center gap-1 rounded-md py-0.5 pl-1.5 text-[11.5px] font-semibold shadow-sm ${
              etiqueta.quitable && onQuitarRehacer ? 'pr-0.5' : 'pr-1.5'
            } ${etiqueta.alerta ? 'bg-[#FFF5F4] text-[#B42318]' : 'bg-white/95 text-brand-black'}`}
          >
            <span className="shrink-0" aria-hidden="true">
              {etiqueta.icono}
            </span>
            <span className="truncate">{etiqueta.texto}</span>
            {etiqueta.quitable && onQuitarRehacer && (
              <button type="button" onClick={onQuitarRehacer} aria-label={`No rehacer ${producto.nombre}`} title="Sacar" className="grid h-4 w-4 shrink-0 place-items-center rounded text-[rgb(var(--brand-black-rgb)_/_0.5)] hover:bg-black/[0.06] hover:text-brand-black">
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            )}
          </span>
        )}
        {estado === 'listo' && !enVuelo && !esTelefono && (
          // Sólo en escritorio: en el teléfono la tarjeta entera abre el visor, que tiene editar y descargar.
          <span className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            {onExpandir && video && (
              <button type="button" onClick={onExpandir} title="Ver en grande" aria-label={`Ver el video de ${producto.nombre} en grande`} className="grid h-7 w-7 place-items-center rounded-md bg-white/90 text-brand-black shadow-sm hover:bg-white">
                <Maximize2 className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
              </button>
            )}
            <button type="button" onClick={onAbrir} title="Ver el guion" aria-label={`Ver el guion de ${producto.nombre}`} className="grid h-7 w-7 place-items-center rounded-md bg-white/90 text-brand-black shadow-sm hover:bg-white">
              <Pencil className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
            </button>
            {onDescargar && (
              <button type="button" onClick={onDescargar} disabled={descargando} title="Descargar" aria-label={`Descargar el video de ${producto.nombre}`} className="grid h-7 w-7 place-items-center rounded-md bg-white/90 text-brand-black shadow-sm hover:bg-white disabled:opacity-60">
                {descargando ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Download className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />}
              </button>
            )}
          </span>
        )}
        {/* La voz, en su contenedor blanco pegado al pie de la tarjeta: tocarla abre el selector
            para ESTE producto. Creándose no va (ya no se cambia y tapa la carga), y con el video
            listo tampoco: ahí están los controles del video. */}
        {!enVuelo && !(estado === 'listo' && video) && (
          <button
            type="button"
            onClick={onElegirVoz}
            title={`Voz: ${nombreDeVoz}. Tocá para cambiarla`}
            className="absolute inset-x-2 bottom-2 flex items-center gap-2.5 rounded-xl bg-white py-2 pl-2 pr-2.5 text-left ring-1 ring-black/[0.06] transition-colors hover:bg-neutral-50"
          >
            <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${sinVoz ? 'bg-black/[0.05] text-[#3C3C43]/60' : 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary'}`}>
              {sinVoz ? <VolumeX className="h-4 w-4" strokeWidth={2} aria-hidden="true" /> : <Mic className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[11px] font-medium leading-tight text-brand-black">Voz</span>
              <span className="truncate text-[11px] font-medium leading-tight text-[#3C3C43]/55">{nombreDeVoz}</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-[rgb(var(--brand-black-rgb)_/_0.35)]" strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </div>
      <figcaption className="flex min-w-0 items-center gap-2 px-0.5">
        {/* El producto: su miniatura y el nombre. */}
        <span className="h-7 w-7 shrink-0 overflow-hidden rounded-md bg-neutral-100 ring-1 ring-black/[0.06]">
          {producto.portadaUrl && <CachedImg src={producto.portadaUrl} alt="" className="h-full w-full object-cover" />}
        </span>
        <span className="truncate text-[13.5px] font-semibold text-brand-black" title={producto.nombre}>
          {producto.nombre}
        </span>
      </figcaption>
    </figure>
  );
};
